import { Checkbox } from "antd";
import type { CheckboxValueType } from "antd/es/checkbox/Group";
import { Control, Controller, FieldValues } from "react-hook-form";
import styled from "styled-components";

interface Props {
  options: {
    label: string;
    value: string;
    checked?: boolean;
    description?: string;
    icon?: string;
    disabled?: boolean;
  }[];
  onChange: (checkedValue: CheckboxValueType[]) => void;
  control: Control<FieldValues>;
  id: string;
  defaultValue?: string[];
  alwaysShowDescription?: boolean;
}

const StyledCheckboxGroup = styled(Checkbox.Group)`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;

  .ant-checkbox-wrapper-checked {
    border: 1px solid var(--blue-300);
    color: var(--blue-300);
    transition: all 0.3s ease-in-out;
  }
  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: var(--blue-300) !important;
    border-color: var(--blue-300) !important;
  }
`;

const StyledCheckbox = styled(Checkbox)<{ $disabled?: boolean }>`
  display: flex;
  gap: 0.5rem;
  padding: 1rem;
  border-radius: 0.5rem;
  border: 1px solid #e2e2e2;
  box-shadow: 2px 2px 10px rgba(0, 0, 0, 0.05);
  width: 100%;

  ${(props) =>
    props.$disabled &&
    `
    opacity: 0.5;
    cursor: not-allowed;
    background-color: #f5f5f5;
    .ant-checkbox-inner {
      background-color: #f5f5f5 !important;
      border-color: #f5f5f5 !important;
    }
  `}
`;

const CheckboxContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const CheckboxLabel = styled.p`
  margin-bottom: 0;
  font-weight: 500;
`;

const CheckboxDescription = styled.span`
  font-size: 12px;
  opacity: 0.8;
`;

function CheckboxGroup({
  options,
  onChange,
  control,
  id,
  defaultValue,
  alwaysShowDescription = false,
}: Props) {
  return (
    <Controller
      name={id}
      control={control}
      defaultValue={defaultValue}
      render={({ field: { onChange: fieldOnChange, ...fields } }) => {
        return (
          <StyledCheckboxGroup
            {...fields}
            onChange={(e) => {
              if (onChange) onChange(e);
              fieldOnChange(e);
            }}
          >
            {options.map((option) => (
              <div key={option.value}>
                <StyledCheckbox
                  value={option.value}
                  checked={option.checked}
                  disabled={option.disabled}
                  $disabled={option.disabled}
                >
                  <CheckboxContent>
                    <CheckboxLabel>
                      {option.icon} &nbsp; {option.label}
                    </CheckboxLabel>
                    {option.description &&
                      (option.checked || alwaysShowDescription) && (
                        <CheckboxDescription>
                          {option.description}
                        </CheckboxDescription>
                      )}
                  </CheckboxContent>
                </StyledCheckbox>
              </div>
            ))}
          </StyledCheckboxGroup>
        );
      }}
    />
  );
}

export default CheckboxGroup;
